import { Link } from "react-router-dom";
import { Heart, Users, Home as HomeIcon, HandHeart, Sparkles, ArrowUpRight, ShieldCheck, DollarSign, Building2, Briefcase, Lightbulb, Package, Handshake, Waves } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import ImpactTransparency from "@/components/ImpactTransparency";
import LatestUpdates from "@/components/LatestUpdates";

const missions = [
  {
    number: "01",
    tag: "New Beginnings™",
    subtitle: "Women & Children After Domestic Violence",
    body: "You experienced something difficult, but your story continues. New Beginnings™ helps women and children rebuild, move forward, and begin what comes next.",
    signature: "The Next Chapter",
    icon: HandHeart,
    to: "/foundation/new-beginnings",
    cta: "Explore New Beginnings",
  },
  {
    number: "02",
    tag: "UNSPOKEN™",
    subtitle: "Men's Mental Health & Wellness",
    body: "You've been carrying something privately, but you don't have to carry it silently. UNSPOKEN™ creates space for men to connect, decompress, prioritize their wellness, and know they don't have to carry everything alone.",
    signature: "Breaking the Silence",
    icon: Waves,
    to: "/foundation/unspoken",
    cta: "Explore UNSPOKEN",
  },
  {
    number: "03",
    tag: "Next Move™",
    subtitle: "Youth & Family Experiences",
    body: "Sometimes what comes next is simply having something good to look forward to. Next Move™ creates meaningful experiences that bring youth and families together, open doors to new opportunities, and create memories that last.",
    signature: "Creating Core Memories",
    icon: Sparkles,
    to: "/foundation/next-move",
    cta: "Explore Next Move",
  },
];


export default function Foundation() {
  useSEO({
    title: "The Tandem T Foundation — New Beginnings · UNSPOKEN",
    description: "The Tandem T Foundation supports women and children rebuilding after domestic violence (New Beginnings) and creates space for men to prioritize their mental health and wellness (UNSPOKEN). A federally recognized 501(c)(3) nonprofit in Florida.",
    path: "/foundation",
  });
  return (
    <div data-testid="page-foundation">
      <PageHero
        testId="foundation-hero"
        overline="The Tandem T Foundation"
        title={<>You <em className="italic">Matter.</em></>}
        subtitle="You are seen. You are heard. You are supported. Because no one should navigate life's hardest chapters alone."
        image="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c"
      />

      {/* BRAND LOGO */}
      <section className="py-16 md:py-24 border-b border-white/10 bg-black" data-testid="foundation-brand-logo">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex justify-center">
          <Reveal>
            <img
              src="/brand/tandemt-foundation-logo.png"
              alt="The Tandem T Foundation Inc"
              data-testid="foundation-brand-logo-img"
              className="w-auto h-64 md:h-80 lg:h-96 object-contain"
              style={{ mixBlendMode: "screen" }}
            />
          </Reveal>
        </div>
      </section>


      {/* MISSION */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="foundation-mission">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-4">
            <p className="overline text-white/55">Our Mission</p>
            <Heart size={22} className="text-white mt-6" />
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-8">
            <p className="font-serif font-light text-white text-xl md:text-3xl leading-snug">
              The Tandem T Foundation is a 501(c)(3) nonprofit organization helping people and families move
              forward through some of life&rsquo;s hardest chapters. Through <em className="italic">New Beginnings&trade;</em>,
              we support women and children rebuilding after domestic violence. Through <em className="italic">UNSPOKEN&trade;</em>,
              we create space for men to prioritize their mental health and wellness through connection, conversation,
              brotherhood, and meaningful experiences. Through <em className="italic">Next Move&trade;</em>, we create
              experiences, mentorship, and opportunities that help youth and families discover what can come next.
            </p>
            <p className="text-white/70 font-light text-lg md:text-xl mt-8">
              Different journeys. Different needs. One belief:{" "}
              <em className="font-serif italic text-white">You Matter.</em>
            </p>
          </Reveal>
        </div>
      </section>

      {/* OUR BELIEF */}
      <section className="py-20 md:py-28 border-b border-white/10 bg-white/[0.02]" data-testid="foundation-belief">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <Reveal><p className="overline text-white/55">Our Belief</p></Reveal>
          <Reveal delay={0.08}>
            <p className="text-white/80 font-light leading-[1.9] text-lg md:text-xl mt-8">
              Every person deserves to feel seen, heard, and supported &mdash; especially during life&rsquo;s hardest chapters.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-white/70 font-light leading-[1.9] text-lg mt-6">
              We believe difficult circumstances should never define someone&rsquo;s worth or determine what comes next.
              With support, connection, compassion, and meaningful opportunities, people can find the strength to
              rebuild, breathe, move forward, and believe in what is still possible.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-white/70 font-light leading-[1.9] text-lg mt-6">
              Because at the heart of everything we do is one simple belief:
            </p>
            <p className="font-serif italic text-white text-4xl md:text-6xl font-light mt-6 tracking-tight">You Matter.</p>
          </Reveal>
        </div>
      </section>

      {/* WHY OUR WORK IS PERSONAL — teaser linking to /foundation/our-story */}
      <section className="relative py-24 md:py-32 border-b border-white/10 overflow-hidden" data-testid="foundation-why">
        <div className="absolute left-[-4%] top-[8%] w-[38%] max-w-[520px] opacity-[0.05] pointer-events-none select-none hidden md:block" aria-hidden>
          <img src="/brand/tandem-t-logo.png" alt="" className="w-full h-auto" style={{ mixBlendMode: "screen" }} />
        </div>

        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Why Our Work Is Personal</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight">
              A Foundation <em className="italic">Built From Experience.</em>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="text-white/75 font-light leading-[1.9] text-lg md:text-xl mt-10 max-w-3xl">
              Founder &amp; President <strong className="font-semibold text-white">Chrissy Thompson</strong> understands
              what it means to rebuild life with children after domestic violence &mdash; to start over, navigate
              uncertainty, and search for stability while creating a <strong className="font-semibold text-white">New Beginning&trade;</strong>. Her experience inspired a mission
              to ensure survivors and their children feel seen, supported, and reminded that rebuilding does not have
              to happen alone.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-white/75 font-light leading-[1.9] text-lg md:text-xl mt-6 max-w-3xl">
              Co-Founder &amp; Vice President <strong className="font-semibold text-white">Rohan Taylor</strong>
              {" "}understands the pressures men can carry as fathers, providers, and protectors &mdash; and how easily
              those struggles can remain <strong className="font-semibold text-white">UNSPOKEN&trade;</strong>. His experiences inspired a mission to create space for men to talk,
              connect, find support, and prioritize their mental and emotional well-being.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="text-white/75 font-light leading-[1.9] text-lg md:text-xl mt-6 max-w-3xl">
              And after some of life&rsquo;s hardest chapters, another question often comes: &ldquo;What&rsquo;s
              next?&rdquo; We know that moving forward can begin with something as simple as a new experience, a new
              connection, or an opportunity to see what is still possible. That understanding inspired{" "}
              <strong className="font-semibold text-white">Next Move&trade;</strong> &mdash; creating meaningful experiences,
              mentorship, and opportunities for youth and families to explore what can come next together.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-12 max-w-3xl">
              <p className="overline text-white/45">Those Experiences Shaped the Heart of Tandem T Foundation</p>
              <div className="mt-6 space-y-3">
                <p className="text-white/80 font-light leading-relaxed text-lg md:text-xl">No family should have to navigate its hardest chapter alone.</p>
                <p className="text-white/80 font-light leading-relaxed text-lg md:text-xl">A difficult chapter should never be the end of someone&rsquo;s story.</p>
                <p className="text-white/80 font-light leading-relaxed text-lg md:text-xl">There is always a next chapter. And sometimes, it begins with the next move.</p>
              </div>
              <p className="font-serif italic text-white text-4xl md:text-6xl font-light mt-8 tracking-tight">You Matter.</p>
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <Link
              to="/foundation/our-story"
              data-testid="foundation-why-readmore"
              className="group inline-flex items-center gap-3 mt-10 overline px-8 py-5 border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
            >
              Read Our Story <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* OUR FOCUS — TWO MISSIONS */}
      <section className="py-24 md:py-32 border-b border-white/10" data-testid="foundation-focus">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Our Programs</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
              Three Programs. <em className="italic">One Belief.</em>
            </h2>
            <p className="text-white/70 font-light leading-relaxed mt-8 max-w-2xl text-lg">
              Each program carries its own purpose and its own meaning &mdash; united by one belief:{" "}
              <em className="italic text-white">You Matter.</em>
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {missions.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.tag} delay={i * 0.08} className="bg-black">
                  <div className="p-10 md:p-14 min-h-[420px] flex flex-col">
                    <div className="flex items-center justify-between">
                      <p className="overline text-white/40">{m.number}</p>
                      <Icon size={22} className="text-white" />
                    </div>
                    <h3 className="font-serif font-light text-white text-3xl md:text-5xl mt-10 leading-[1.05] tracking-tight">
                      {m.tag}
                    </h3>
                    <p className="overline text-white/55 mt-4">{m.subtitle}</p>
                    <p className="text-white/70 font-light leading-relaxed mt-6 flex-1">{m.body}</p>
                    <div className="mt-8 pt-6 border-t border-white/15">
                      <p className="overline text-white/45">Signature Initiative</p>
                      <p className="font-serif italic text-white text-xl md:text-2xl font-light mt-3">{m.signature}</p>
                      <Link
                        to={m.to}
                        data-testid={`foundation-mission-link-${m.number}`}
                        className="group inline-flex items-center gap-3 mt-6 overline text-white/70 hover:text-white transition-colors"
                      >
                        {m.cta} <ArrowUpRight size={15} className="hover-arrow" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Closing belief */}
          <Reveal delay={0.24}>
            <div className="mt-14 text-center max-w-3xl mx-auto">
              <p className="overline text-white/45">Different journeys. Same belief.</p>
              <p className="font-serif italic text-white text-2xl md:text-4xl font-light leading-snug mt-5">
                Whatever the chapter, you don&rsquo;t have to face it alone.
              </p>
              <p className="font-serif text-white text-3xl md:text-5xl font-light mt-6 tracking-tight">You Matter.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NEXT MOVE TEASER */}
      <section className="py-20 md:py-28 border-b border-white/10" data-testid="foundation-next-move-teaser">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-cyan-600 to-emerald-500 p-10 md:p-16">
              <div className="absolute -top-24 -right-16 w-[34rem] h-[34rem] rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-8">
                  <p className="overline text-white/80">Our Youth &amp; Family Initiative</p>
                  <span className="inline-flex items-center font-sans leading-none text-white text-5xl md:text-7xl mt-4">
                    <span className="font-extrabold tracking-tight">Next</span>
                    <span className="italic font-light ml-[0.35em] mr-[0.15em]">Move</span>
                    <svg width="46" height="28" viewBox="0 0 50 30" fill="none" className="text-cyan-100 -ml-1 -mt-[0.25em]" aria-hidden="true">
                      <path d="M2 22 C 14 22, 30 18, 44 6" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                      <path d="M33 5 L46 4 L44 17" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                    <sup className="text-[0.32em] font-normal ml-[0.1em] -translate-y-[0.2em]">™</sup>
                  </span>
                  <p className="font-serif italic text-white/95 text-2xl md:text-3xl font-light leading-snug mt-6 max-w-2xl">
                    When life asks, &ldquo;What&rsquo;s next?&rdquo; we help make the Next Move possible.
                  </p>
                  <p className="text-white/85 font-light leading-relaxed mt-5 max-w-2xl">
                    Through experiences, mentorship, and career &amp; educational exposure, we help young people and
                    families discover what can come next &mdash; and sponsors can make a single experience possible.
                  </p>
                </div>
                <div className="lg:col-span-4 flex lg:justify-end">
                  <Link
                    to="/foundation/next-move"
                    data-testid="foundation-next-move-cta"
                    className="group inline-flex items-center gap-3 overline px-8 py-5 bg-white text-cyan-700 hover:bg-cyan-50 transition-colors rounded-full"
                  >
                    Explore Next Move <ArrowUpRight size={16} className="hover-arrow" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* LATEST UPDATES (dynamic, hidden if none) */}
      <LatestUpdates />

      {/* IMPACT (transparency lives on the Our Work page) */}
      <ImpactTransparency showTransparency={false} />

      {/* DONATE — CASH APP */}
      <section id="donate" className="relative py-24 md:py-32 border-b border-white/10 overflow-hidden scroll-mt-24" data-testid="foundation-donate">
        <div className="absolute left-[-6%] top-[10%] w-[40%] max-w-[520px] opacity-[0.05] pointer-events-none select-none hidden md:block" aria-hidden>
          <img src="/brand/tandem-t-logo.png" alt="" className="w-full h-auto" style={{ mixBlendMode: "screen" }} />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* LEFT — message + payment options */}
          <div className="md:col-span-12">
            <Reveal>
              <p className="overline text-white/55">Give to the Foundation</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight">
                Somewhere tonight, a woman is packing what fits in <em className="italic">one bag.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="font-serif italic text-white/85 text-xl md:text-2xl font-light leading-snug mt-8 max-w-2xl">
                And somewhere, a man is carrying more than anyone around him knows. Somewhere else, a young person
                or family is wondering what comes next. Your gift reminds them all:{" "}
                <em className="italic text-white">You Matter.</em>
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 max-w-2xl space-y-5">
                <p className="text-white/75 font-light leading-relaxed text-lg">
                  Right now, The Tandem T Foundation is helping people and families move forward through some of
                  life&rsquo;s hardest chapters. Through <strong className="font-semibold text-white">New Beginnings&trade;</strong>,
                  we support women and children rebuilding after domestic violence. Through{" "}
                  <strong className="font-semibold text-white">UNSPOKEN&trade;</strong>, we create spaces for men to
                  prioritize their mental health and wellness. Through <strong className="font-semibold text-white">Next Move&trade;</strong>,
                  we create meaningful experiences, mentorship, and opportunities that help youth and families discover
                  what can come next.
                </p>
                <p className="text-white/75 font-light leading-relaxed text-lg">
                  Your support helps provide essential resources, meaningful experiences, connection, mentorship, and
                  community partnerships across our programs. Every gift carries the same message: You are seen. You are
                  heard. You are supported. <em className="italic text-white">You Matter.</em>
                </p>
                <p className="text-white/75 font-light leading-relaxed text-lg">
                  Because no one should navigate life&rsquo;s hardest chapters alone. Hope Begins With Partnership
                  &mdash; let it begin with <em className="italic text-white">you.</em>
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.24}>
              <Link
                to="/foundation/donate"
                data-testid="foundation-donate-online"
                className="group inline-flex items-center gap-3 mt-10 overline px-8 py-5 bg-white text-black hover:bg-white/85 transition-colors"
              >
                Give Securely by Card <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
              <p className="text-white/50 text-sm font-light mt-4">
                One-time or monthly &mdash; choose your amount on the next screen.
              </p>
            </Reveal>

            <Reveal delay={0.26}>
              <div className="mt-10 border border-white/20 p-6 md:p-8 bg-white/[0.03] max-w-md" data-testid="foundation-more-ways">
                <p className="overline text-white/45">More Ways to Give</p>
                <p className="text-white/75 font-light mt-3 leading-relaxed">
                  Prefer an app? Give instantly through{" "}
                  <strong className="text-white font-semibold">Venmo</strong>,{" "}
                  <strong className="text-white font-semibold">Cash App</strong>,{" "}
                  <strong className="text-white font-semibold">Zelle</strong>, or{" "}
                  <strong className="text-white font-semibold">PayPal</strong>.
                </p>
                <Link
                  to="/foundation/donate#give-instantly"
                  data-testid="foundation-donate-apps"
                  className="group inline-flex items-center gap-3 mt-6 overline px-6 py-4 border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
                >
                  See All Payment Options
                  <ArrowUpRight size={14} className="hover-arrow" />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.32}>
              <p className="text-xs text-white/40 font-light leading-relaxed mt-8 max-w-lg">
                Tandem T Foundation is a federally recognized 501(c)(3) nonprofit organization. Contributions
                are tax-deductible to the extent permitted by law and are used exclusively to advance our
                charitable mission.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE TEAM — teaser to /foundation/team */}
      <section className="py-24 md:py-32 border-b border-white/10" data-testid="foundation-team-teaser">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-8">
            <p className="overline text-white/55">The Team Behind the Dream</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight">
              Real people, <em className="italic">a shared promise.</em>
            </h2>
            <p className="text-white/70 font-light leading-relaxed mt-8 max-w-2xl text-lg">
              Executive leadership, a dedicated Board of Directors, and a Youth Advisory Team building this
              Foundation with the next generation &mdash; not just for them.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4 md:text-right">
            <Link
              to="/foundation/team"
              data-testid="foundation-team-link"
              className="group inline-flex items-center gap-3 overline px-8 py-5 border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
            >
              Meet the Team <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
